import { Avatar, Box, Drawer, Stack, Typography } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { showMessages as intercomShowMessagingComponent } from '@intercom/messenger-js-sdk';
import { MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { useCognitoAuth } from '../../auth/cognito-auth-context';
import { userState } from 'state';

import { ProducerNavModule } from './producer-nav-config';
import { ProfileMenu } from './profile-menu';

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  modules: ProducerNavModule[];
  activeModule: string | null;
}

export function MobileDrawer({
  open,
  onClose,
  modules,
  activeModule,
}: MobileDrawerProps) {
  const navigate = useNavigate();
  const user = useRecoilValue(userState);
  const { impersonateUserId } = useCognitoAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleModuleClick = (mod: ProducerNavModule) => {
    if (mod.href) {
      navigate(mod.href);
      onClose();
    }
  };

  const handleAvatarClick = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const initial = user?.first_name?.charAt(0).toUpperCase() || '?';

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        display: { xs: 'block', md: 'none' },
        '& .MuiDrawer-paper': {
          width: 200,
          bgcolor: 'background.default',
        },
      }}
    >
      <Stack sx={{ height: '100%', pt: 4, py: 2, px: 1.5 }}>
        {/* Nav items */}
        <Stack spacing={0.5} sx={{ flex: 1 }}>
          {modules.map((mod) => {
            const isActive = mod.id === activeModule;
            return (
              <Stack
                key={mod.id}
                direction="row"
                alignItems="center"
                gap={1.5}
                onClick={() => handleModuleClick(mod)}
                sx={{
                  px: 1.5,
                  py: 2,
                  cursor: 'pointer',
                  borderRadius: 2,
                  bgcolor: isActive ? 'action.selected' : 'transparent',
                  '&:active': { bgcolor: 'action.hover' },
                }}
              >
                <Box
                  sx={{
                    color: isActive ? 'primary.main' : 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 36,
                  }}
                >
                  {mod.icon}
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'primary.main' : 'text.primary',
                  }}
                >
                  {mod.label}
                </Typography>
              </Stack>
            );
          })}
        </Stack>

        {/* Bottom: Help + Profile */}
        <Stack spacing={0.5}>
          <Stack
            direction="row"
            alignItems="center"
            gap={2}
            onClick={() => {
              intercomShowMessagingComponent();
              onClose();
            }}
            sx={{
              px: 1.5,
              py: 1,
              cursor: 'pointer',
              borderRadius: 2,
              '&:active': { bgcolor: 'action.hover' },
            }}
          >
            <HelpOutline sx={{ fontSize: 36, color: 'text.secondary' }} />
            <Typography variant="body1" color="text.primary">
              Help
            </Typography>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            onClick={handleAvatarClick}
            sx={{
              px: 1.5,
              py: 1,
              cursor: 'pointer',
              borderRadius: 2,
              '&:active': { bgcolor: 'action.hover' },
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'primary.main',
                color: 'background.default',
                fontSize: '0.75rem',
                fontWeight: 600,
                ...(impersonateUserId && {
                  border: '2px solid',
                  borderColor: 'error.main',
                }),
              }}
            >
              {initial}
            </Avatar>
            <Typography variant="body1" color="text.primary">
              Profile
            </Typography>
          </Stack>

          <ProfileMenu
            anchorEl={anchorEl}
            onClose={handleClosePopover}
            onLogout={() => {
              onClose();
              setTimeout(() => {
                navigate('/login');
              }, 100);
            }}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          />
        </Stack>
      </Stack>
    </Drawer>
  );
}
